import { useState } from 'react';
import { Alert, Badge, Button, Group, Paper, Stack, Stepper, Text, Textarea, Title, Tooltip } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import type { TransferWrite } from '@zebra/core';
import { apiFetch, ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { AnyLocationSelect } from '../../../features/stores-transfers/components/AnyLocationSelect';
import { LineRowsEditor, emptyLineRow, type LineFormRow } from '../../../features/stores-transfers/components/LineRowsEditor';
import { LedgerPreview } from '../../../features/stores-transfers/components/LedgerPreview';
import { useCreateTransfer } from '../../../features/stores-transfers/api';

/** Owned by G24 (plan.md S0). Guided "create a transfer" flow (BUILD_SPEC.md §8.6):
 * from-location -> to-location (same-site or cross-site falls out of which locations
 * are chosen) -> lines -> review with a ledger-effect preview -> Post. */
export const Route = createFileRoute('/_authenticated/stores-transfers/transfers/new')({
  component: NewTransferPage,
});

function NewTransferPage() {
  const canOperate = useCanOperate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createTransfer = useCreateTransfer();

  const [active, setActive] = useState(0);
  const [fromLocationId, setFromLocationId] = useState('');
  const [fromSiteId, setFromSiteId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [toSiteId, setToSiteId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineFormRow[]>([emptyLineRow()]);
  const [postError, setPostError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  const routeValid = Boolean(fromLocationId && toLocationId && fromLocationId !== toLocationId);
  const linesValid = lines.length > 0 && lines.every((l) => l.productId && l.qty && Number(l.qty) > 0);
  const crossSite = Boolean(fromSiteId && toSiteId && fromSiteId !== toSiteId);

  function buildPayload(): TransferWrite {
    return {
      fromLocationId,
      toLocationId,
      fromSiteId,
      toSiteId,
      notes: notes.trim() || null,
      lines: lines.map((l) => ({ productId: l.productId, qty: l.qty })),
    };
  }

  async function handleSaveDraft() {
    setPostError(null);
    try {
      const created = await createTransfer.mutateAsync(buildPayload());
      void navigate({ to: '/stores-transfers/transfers/$transferId', params: { transferId: created.id } });
    } catch {
      // `createTransfer.isError`/`.error` already drive the red text below.
    }
  }

  async function handlePost() {
    setPostError(null);
    setPosting(true);
    try {
      const created = await createTransfer.mutateAsync(buildPayload());
      try {
        await apiFetch(`/stores/transfers/${created.id}/post`, { method: 'POST' });
        await queryClient.invalidateQueries({ queryKey: ['stores', 'transfers'] });
      } catch (err) {
        const message = err instanceof ApiRequestError ? err.message : 'Posting failed unexpectedly.';
        setPostError(`Draft ${created.number} was saved, but posting failed: ${message} You can retry from the transfer.`);
      }
      void navigate({ to: '/stores-transfers/transfers/$transferId', params: { transferId: created.id } });
    } catch {
      // Creation itself failed (e.g. validation) — `createTransfer.isError`/`.error`
      // already drive the red text below; nothing further to do here beyond not
      // leaving an unhandled rejection.
    } finally {
      setPosting(false);
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Stack gap={2}>
        <Title order={2}>New transfer</Title>
        <Text c="dimmed" size="sm">
          Move stock between locations, same-site or cross-site.
        </Text>
      </Stack>

      {postError && (
        <Alert color="amber" icon={<IconAlertTriangle size={18} />} title="Partial success">
          {postError}
        </Alert>
      )}

      <Stepper active={active} onStepClick={setActive}>
        <Stepper.Step label="Route" description="From &amp; to">
          <Paper withBorder radius="md" p="md" mt="md">
            <Stack gap="md">
              <AnyLocationSelect
                label="From location"
                value={fromLocationId}
                onChange={(id, _label, siteId) => {
                  setFromLocationId(id);
                  setFromSiteId(siteId);
                }}
                required
              />
              <AnyLocationSelect
                label="To location"
                value={toLocationId}
                excludeId={fromLocationId || undefined}
                onChange={(id, _label, siteId) => {
                  setToLocationId(id);
                  setToSiteId(siteId);
                }}
                required
              />
              {fromLocationId && toLocationId && (
                <Badge color={crossSite ? 'amber' : 'graphite'} variant="light" w="fit-content">
                  {crossSite ? 'Cross-site transfer' : 'Same-site transfer'}
                </Badge>
              )}
              <Textarea label="Notes" value={notes} onChange={(event) => setNotes(event.currentTarget.value)} autosize minRows={2} />
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Lines" description="What to move">
          <Paper withBorder radius="md" p="md" mt="md">
            <LineRowsEditor rows={lines} onChange={setLines} siteId={fromSiteId || undefined} includeLocation={false} />
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Review" description="Ledger effect &amp; Post">
          <Paper withBorder radius="md" p="md" mt="md">
            <LedgerPreview
              siteId={fromSiteId || undefined}
              lines={lines}
              attribution={crossSite ? 'Cross-site — value moves between site pools' : 'Same-site — value stays within one site pool'}
            />
          </Paper>
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between">
        <Button variant="default" onClick={() => setActive((s) => Math.max(0, s - 1))} disabled={active === 0}>
          Back
        </Button>
        {active < 2 ? (
          <Button
            onClick={() => setActive((s) => Math.min(2, s + 1))}
            disabled={(active === 0 && !routeValid) || (active === 1 && !linesValid)}
          >
            Continue
          </Button>
        ) : (
          <Group>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button variant="default" disabled={!canOperate} onClick={handleSaveDraft} loading={createTransfer.isPending && !posting}>
                  Save as draft
                </Button>
              </span>
            </Tooltip>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button onClick={handlePost} loading={posting} disabled={!canOperate || !routeValid || !linesValid}>
                  Post
                </Button>
              </span>
            </Tooltip>
          </Group>
        )}
      </Group>
      {createTransfer.isError && !postError && (
        <Text c="red" size="sm">
          {createTransfer.error instanceof Error ? createTransfer.error.message : 'Something went wrong creating the transfer.'}
        </Text>
      )}
    </Stack>
  );
}
